import { useCallback, useReducer } from "react";
import ApplicationContext from "./ApplicationContext";
import ApplicationReducer from "./ApplicationReducer";
import {
  SET_MY_APPLICATIONS,
  SET_PENDING_APPLICATIONS,
  SET_APPLICATIONS_LOADING,
  APPLICATIONS_LOADED,
  SET_LANDLORD_REQUESTS,
  SET_LANDLORD_REQUESTS_LOADING,
  SET_PENDING_SKOR_PROTECT_ACTIONS,
} from "../types";
import axiosInstance from "../../services/authWrapper";

const ApplicationState = (props) => {
  const initialState = {
    myApplications: [],
    pendingApplications: [],
    loading: false,
    loaded: false,
    landlordRequests: [],
    landlordRequestsLoading: false,
    pendingSkorProtectActions: [],
    pendingActionCount: 0,
  };

  const [state, dispatch] = useReducer(ApplicationReducer, initialState);

  const headers = () => ({
    headers: { "Accept-Language": localStorage.getItem("i18nextLng") },
  });

  const loadPendingSkorProtectActions = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/applications/pending-skor-protect-actions`,
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng"),
          },
        }
      );
      const actions = res.data?.success_res?.actions;
      const pendingActionCount = res.data?.success_res?.pending_actions;
      dispatch({
        type: SET_PENDING_SKOR_PROTECT_ACTIONS,
        payload: {
          actions: Array.isArray(actions) ? actions : [],
          pendingActionCount: Number.isFinite(pendingActionCount)
            ? pendingActionCount
            : Array.isArray(actions)
              ? actions.filter(
                  (action) => action?.status === "waiting_for_payment"
                ).length
              : 0,
        },
      });
    } catch {
      // Keep the last successful result when a polling request fails.
    }
  }, []);

  const createSkorProtectPaymentLink = async (skorProtectId) => {
    const res = await axiosInstance.post(
      `${process.env.REACT_APP_SERVER_URL}/applications/pending-skor-protect-actions/${skorProtectId}/payment-link`,
      {},
      headers()
    );
    return res.data?.success_res?.paymentUrl;
  };

  const getSkorProtectPaymentReceipt = async (skorProtectId) => {
    const res = await axiosInstance.get(
      `${process.env.REACT_APP_SERVER_URL}/applications/pending-skor-protect-actions/${skorProtectId}/receipt`,
      headers()
    );
    return res.data?.success_res?.receipt_url;
  };

  /* fetch both lists and store in context */
  const _fetchBoth = async () => {
    dispatch({ type: SET_APPLICATIONS_LOADING, payload: true });
    try {
      const [myRes, pendingRes] = await Promise.all([
        axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/applications/my-applications`,
          headers()
        ),
        axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/applications/pending`,
          headers()
        ),
      ]);
      dispatch({
        type: SET_MY_APPLICATIONS,
        payload: myRes.data?.success_res ?? [],
      });
      dispatch({
        type: SET_PENDING_APPLICATIONS,
        payload: pendingRes.data?.success_res ?? [],
      });
    } catch {
      // silently ignore
    } finally {
      dispatch({ type: SET_APPLICATIONS_LOADING, payload: false });
    }
  };

  /* initial load — called once from skorList; guards with loaded flag */
  const loadApplicationsData = async () => {
    await Promise.all([_fetchBoth(), loadPendingSkorProtectActions()]);
    dispatch({ type: APPLICATIONS_LOADED });
  };

  /* force refresh — called after create / consent */
  const reloadApplications = async () => {
    await Promise.all([_fetchBoth(), loadPendingSkorProtectActions()]);
  };

  /* create a new application */
  const createApplication = async (payload) => {
    const res = await axiosInstance.post(
      `${process.env.REACT_APP_SERVER_URL}/applications`,
      payload,
      headers()
    );
    await reloadApplications();
    return res.data;
  };

  /* share an application with a landlord */
  const shareApplicationWithLandlord = async (landlordId, applicationId) => {
    const res = await axiosInstance.post(
      `${process.env.REACT_APP_SERVER_URL}/applications/share`,
      {
        landlord_id: landlordId,
        ...(applicationId ? { application_id: applicationId } : {}),
      },
      headers()
    );
    await reloadApplications();
    return res.data;
  };

  /* accept a landlord share request — tenant chooses what to share */
  const acceptLandlordRequest = async (
    requestId,
    applicationId,
    skorProtect
  ) => {
    const res = await axiosInstance.patch(
      `${process.env.REACT_APP_SERVER_URL}/applications/landlord-requests/${requestId}/respond`,
      {
        status: "accepted",
        ...(applicationId ? { application_id: applicationId } : {}),
        ...(skorProtect !== undefined ? { skor_protect: skorProtect } : {}),
      },
      headers()
    );
    await loadLandlordRequests();
    return res.data;
  };

  /* reject a landlord share request */
  const rejectLandlordRequest = async (requestId) => {
    const res = await axiosInstance.patch(
      `${process.env.REACT_APP_SERVER_URL}/applications/landlord-requests/${requestId}/respond`,
      { status: "rejected" },
      headers()
    );
    await loadLandlordRequests();
    return res.data;
  };

  /* load landlord pending requests for the tenant */
  const loadLandlordRequests = async () => {
    dispatch({ type: SET_LANDLORD_REQUESTS_LOADING, payload: true });
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/applications/landlord-requests`,
        headers()
      );
      dispatch({
        type: SET_LANDLORD_REQUESTS,
        payload: res.data?.success_res ?? [],
      });
    } catch {
      // silently ignore
    } finally {
      dispatch({ type: SET_LANDLORD_REQUESTS_LOADING, payload: false });
    }
  };

  /* accept or reject an invitation */
  const consentApplication = async (applicationId, consent_status) => {
    const res = await axiosInstance.patch(
      `${process.env.REACT_APP_SERVER_URL}/applications/${applicationId}/consent`,
      { consent_status },
      headers()
    );
    await reloadApplications();
    return res.data;
  };

  /* delete an application */
  const deleteApplication = async (applicationId) => {
    const res = await axiosInstance.delete(
      `${process.env.REACT_APP_SERVER_URL}/applications/${applicationId}`,
      headers()
    );
    await reloadApplications();
    return res.data;
  };

  /* register that tenant opened a landlord invitation link */
  const openLandlordLink = async (requestProfileLink, propertyId) => {
    const payload = { requestProfileLink };
    if (propertyId) payload.property_id = propertyId;
    const res = await axiosInstance.post(
      `${process.env.REACT_APP_SERVER_URL}/applications/tenant-opened-landlord-link`,
      payload,
      headers()
    );
    return res.data;
  };

  /* fetch applications for a specific tenant shared into landlord's workspace */
  const fetchLandlordTenantApplications = async (sharedId) => {
    const res = await axiosInstance.get(
      `${process.env.REACT_APP_SERVER_URL}/applications/landlord?sharedId=${sharedId}`,
      headers()
    );
    return res.data?.success_res ?? [];
  };

  return (
    <ApplicationContext.Provider
      value={{
        myApplications: state.myApplications,
        pendingApplications: state.pendingApplications,
        applicationsLoading: state.loading,
        applicationsLoaded: state.loaded,
        landlordRequests: state.landlordRequests,
        landlordRequestsLoading: state.landlordRequestsLoading,
        pendingSkorProtectActions: state.pendingSkorProtectActions,
        pendingActionCount: state.pendingActionCount,
        loadApplicationsData,
        reloadApplications,
        createApplication,
        consentApplication,
        deleteApplication,
        shareApplicationWithLandlord,
        loadLandlordRequests,
        loadPendingSkorProtectActions,
        createSkorProtectPaymentLink,
        getSkorProtectPaymentReceipt,
        acceptLandlordRequest,
        rejectLandlordRequest,
        fetchLandlordTenantApplications,
        openLandlordLink,
      }}
    >
      {props.children}
    </ApplicationContext.Provider>
  );
};

export default ApplicationState;
